import { useState, useEffect } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import ConfirmModal from "../employee/delete_modal";
import Dropdown from "../../components/modal/dropdown";
import Can from "../common/can";
import { toast } from "react-toastify";
import "./department.css";
import { getDepartments, deleteDepartment } from "../services/departmentservice";
import { useAuth } from "../context/authcontext";
import Pagination from "../common/pagination";
import {
  DepartmentStatus,
  DepartmentStatusOptions,
  getDepartmentStatusLabel,
  getDepartmentStatusClass,
} from "../../constants/constants";
import DepartmentFormModal from "./adddepartment";

const PAGE_SIZE = 20;

const Department = () => {
  const { hasAnyPermission, hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const canShowActions = isAdmin || hasAnyPermission(["department.update", "department.delete"]);

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(DepartmentStatus.ALL);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [editingDept, setEditingDept] = useState(null);

  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      // Note: Backend currently only returns active departments, so statusFilter might not do much
      // unless backend supports it.

      const res = await getDepartments(params);
      const data = res.data || res;
      const meta = res.meta || {};
      setDepartments(Array.isArray(data) ? data : data.results || []);
      setTotalCount(meta.count ?? data.count ?? (Array.isArray(data) ? data.length : 0));
    } catch (error) {
      console.error(error);
      toast.error("Failed to load departments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const openAddModal = () => {
    setEditingDept(null);
    setOpenModal(true);
  };

  const openEditModal = (dept) => {
    setEditingDept(dept);
    setOpenModal(true);
  };

  const confirmDelete = async (password) => {
    try {
      await deleteDepartment(deleteTarget.id, password);
      toast.success("Department deleted successfully");
      setDeleteTarget(null);
      await fetchDepartments();
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        error.response?.data?.error ||
        (typeof error.response?.data === "string" ? error.response.data : null) ||
        "Failed to delete department. Please check your password and try again.";
      throw new Error(msg);
    }
  };

  /* ── FILTER ── */
  const filtered = departments.filter((dept) => {
    switch (statusFilter) {
      case DepartmentStatus.ACTIVE:
        return dept.is_active === true;
      case DepartmentStatus.INACTIVE:
        return dept.is_active === false;
      default:
        return true;
    }
  });

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const paginated = filtered;

  return (
    <div className="container">
      {/* HEADER */}
      <div className="page-header">
        <h2>Departments</h2>

        <Can perm="department.create">
          <button className="primary-btn" onClick={openAddModal}>
            Add New Department
          </button>
        </Can>
      </div>

      {/* FILTERS */}
      <div className="filters-card single">
        <input
          className="search-input"
          placeholder="🔍 Search by Department Name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <Dropdown
          options={DepartmentStatusOptions}
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="Status"
        />
      </div>

      {/* TABLE */}
      {loading ? (
        <div className="empty-state">
          <p>Loading Departments...</p>
        </div>
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>Department Name</th>
                <th>Description</th>
                <th>Status</th>
                {canShowActions && <th>Actions</th>}
              </tr>
            </thead>

            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={canShowActions ? "4" : "3"}>
                    <div className="empty-state">
                      <img src="/images/department.svg" alt="No departments" className="empty-img" />
                      <p>No Departments Found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginated.map((dept) => (
                  <tr key={dept.id}>
                    <td>
                      <strong>{dept.name}</strong>
                    </td>

                    <td>{dept.description || "-"}</td>

                    <td>
                      <span className={`emp-status ${getDepartmentStatusClass(dept.is_active)}`}>
                        {getDepartmentStatusLabel(dept.is_active)}
                      </span>
                    </td>

                    {canShowActions && (
                      <td className="action-icons">
                        <Can perm="department.update">
                          <button className="icon-btn edit" onClick={() => openEditModal(dept)}>
                            <FaEdit />
                          </button>
                        </Can>

                        <Can perm="department.delete">
                          <button className="icon-btn delete" onClick={() => setDeleteTarget(dept)}>
                            <FaTrash />
                          </button>
                        </Can>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <Pagination page={page} totalPages={totalPages} setPage={setPage} />
        </>
      )}

      {openModal && (
        <DepartmentFormModal
          editingDept={editingDept}
          onClose={() => setOpenModal(false)}
          onSaved={() => { fetchDepartments(); setOpenModal(false); }}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete Department"
          message={`Are you sure you want to delete "${deleteTarget.name}"?`}
          confirmText="Delete"
          type="danger"
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default Department;